// src/components/negocios/NegocioCard.jsx
import React, { useMemo, useState, useCallback } from 'react';
import {
  Shuffle,
  CheckCircle2,
  ChevronDown,
  Edit,
  MessageCircle,
  User2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { formatBRL } from '../dashboard/mockData';
import { useAuth } from '../../context/AuthContext';
import { registrarVisitaRealizadaNoNegocio } from '../../services/negociosService';

/* =========================================================
    Helpers
========================================================= */
function formatFaixaValor(negocio) {
  if (negocio.valorPretendido) return formatBRL(negocio.valorPretendido);
  if (negocio.valorMin || negocio.valorMax) {
    return `${negocio.valorMin ? formatBRL(negocio.valorMin) : '—'}${
      negocio.valorMax ? ' - ' + formatBRL(negocio.valorMax) : ''
    }`;
  }
  return 'Não informado';
}

function bigValorNumber(negocio) {
  return (
    negocio.valorPretendido ??
    negocio.valorMax ??
    negocio.valorMin ??
    negocio.valor ??
    null
  );
}

function abreviarBRL(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const abs = Math.abs(n);

  if (abs >= 1_000_000_000) {
    const v = (n / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 0 : 1);
    return `R$ ${String(v).replace('.', ',')} Bi`;
  }
  if (abs >= 1_000_000) {
    const v = (n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1);
    return `R$ ${String(v).replace('.', ',')} Mi`;
  }
  if (abs >= 1_000) {
    const v = (n / 1_000).toFixed(abs >= 100_000 ? 0 : 1);
    return `R$ ${String(v).replace('.', ',')} mil`;
  }

  return formatBRL(n);
}

function buildWhatsAppLink(rawNumber, message = '') {
  const digits = String(rawNumber || '').replace(/[^\d]/g, '');
  if (!digits) return null;

  const phone = digits.startsWith('55') ? digits : `55${digits}`;
  const base = `https://wa.me/${phone}`;
  const text = String(message || '').trim();
  if (!text) return base;
  return `${base}?text=${encodeURIComponent(text)}`;
}

function initials(name = '') {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '—';
  const a = parts[0]?.[0] || '';
  const b = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
  return (a + b).toUpperCase();
}

function aceitaPermutaBool(n) {
  return !!n?.aceitaPermuta || !!n?.permuta?.aceita;
}

function calcFlexibilidade(negocio) {
  const bairros = negocio?.bairros || negocio?.bairrosDesejados || [];
  const count = Array.isArray(bairros) ? bairros.length : 0;

  const explicit =
    negocio?.flexibilidadeNivel ||
    negocio?.flexibilidade ||
    negocio?.nivelFlexibilidade ||
    '';

  const exp = String(explicit || '').toLowerCase();
  if (exp.includes('alta'))
    return { level: 'alta', label: 'Alta flexibilidade' };
  if (exp.includes('media') || exp.includes('média'))
    return { level: 'media', label: 'Flexibilidade média' };
  if (exp.includes('baixa'))
    return { level: 'baixa', label: 'Baixa flexibilidade' };

  if (count >= 4) return { level: 'alta', label: 'Alta flexibilidade' };
  if (count >= 2) return { level: 'media', label: 'Flexibilidade média' };
  return { level: 'baixa', label: 'Baixa flexibilidade' };
}

function flexDotClass(level) {
  if (level === 'alta') return 'bg-emerald-500';
  if (level === 'media') return 'bg-amber-500';
  return 'bg-zinc-400';
}

function bairrosResumo(bairros = []) {
  const n = Array.isArray(bairros) ? bairros.length : 0;
  if (n === 0) return { short: 'Bairros não informados', long: '' };
  if (n === 1)
    return { short: 'Aceita 1 bairro', long: `Foco em: ${bairros[0]}` };
  if (n <= 3)
    return {
      short: `Aceita ${n} bairros`,
      long: `Aceita: ${bairros.join(' ou ')}`,
    };
  return {
    short: `Aceita vários bairros (${n})`,
    long: `Aceita vários bairros (${n})`,
  };
}

function WhatsAppIcon({ className = 'w-3.5 h-3.5' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M19.11 17.23c-.28-.14-1.64-.81-1.9-.9-.25-.1-.44-.14-.62.14-.18.28-.71.9-.87 1.09-.16.18-.32.2-.6.07-.28-.14-1.18-.43-2.25-1.38-.83-.74-1.39-1.66-1.55-1.94-.16-.28-.02-.43.12-.57.12-.12.28-.32.41-.48.14-.16.18-.28.28-.46.1-.18.05-.35-.02-.48-.07-.14-.62-1.5-.85-2.05-.22-.54-.44-.46-.62-.47l-.53-.01c-.18 0-.48.07-.73.35-.25.28-.96.94-.96 2.3 0 1.36.98 2.67 1.12 2.86.14.18 1.93 2.95 4.67 4.13.65.28 1.16.45 1.56.58.65.21 1.25.18 1.72.11.52-.08 1.64-.67 1.87-1.32.23-.65.23-1.21.16-1.32-.07-.11-.25-.18-.53-.32z" />
      <path d="M26.67 5.33C23.83 2.5 20.05.94 16.04.94 7.78.94 1.07 7.65 1.07 15.91c0 2.64.69 5.21 2 7.48L.94 31.06l7.9-2.07c2.18 1.19 4.63 1.82 7.2 1.82h.01c8.26 0 14.97-6.71 14.97-14.97 0-4-1.56-7.78-4.39-10.51zm-10.62 22.9h-.01c-2.24 0-4.44-.6-6.36-1.74l-.46-.27-4.69 1.23 1.25-4.57-.3-.47c-1.25-1.98-1.91-4.27-1.91-6.63 0-6.93 5.64-12.57 12.58-12.57 3.36 0 6.52 1.31 8.9 3.68 2.38 2.38 3.69 5.54 3.69 8.9 0 6.93-5.64 12.57-12.69 12.57z" />
    </svg>
  );
}

/* =========================================================
    NegocioCard — Card inteiro clicável + hover azul
    ✅ Em "meus negócios" mostra nome do cliente no bloco AUTOR
========================================================= */
export default function NegocioCard({
  negocio,
  modo = 'meu',
  onOpenDetails,
  onEditar,
  onVerContatos,
  onMatchClick,
  matchAberto = false,
  onVisitaRegistrada,
  whatsappCorretorLink,
  showWhatsAppCorretor = true,
  density = 'compact',
}) {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [loadingVisita, setLoadingVisita] = useState(false);

  const isCompact = density === 'compact';

  const tipoImovel =
    String(negocio?.tipoImovel || negocio?.tipo || '').trim() || '—';

  const objetivo =
    String(
      negocio?.objetivoLabel ||
        negocio?.objetivo ||
        (negocio?.buscaTags?.length ? negocio.buscaTags[0] : '') ||
        ''
    ).trim() || '—';

  const cidade =
    String(negocio?.cidade || negocio?.cidadePrincipal || '').trim() ||
    'Cidade não informada';

  const regiao =
    String(negocio?.regiao || negocio?.zona || negocio?.setor || '').trim() ||
    '';

  const bairros = useMemo(() => {
    const arr = negocio?.bairros || negocio?.bairrosDesejados || [];
    return Array.isArray(arr)
      ? arr.map((b) => String(b).trim()).filter(Boolean)
      : [];
  }, [negocio]);

  const valorNum = bigValorNumber(negocio);

  const ticket = useMemo(() => {
    const vMin = Number(negocio?.valorMin ?? null);
    const vMax = Number(negocio?.valorMax ?? null);
    const vPret = Number(negocio?.valorPretendido ?? null);

    if (
      Number.isFinite(vMin) &&
      Number.isFinite(vMax) &&
      vMin > 0 &&
      vMax > 0
    ) {
      return `${abreviarBRL(vMin)} – ${abreviarBRL(vMax)}`;
    }
    if (Number.isFinite(vPret) && vPret > 0) return abreviarBRL(vPret);
    return abreviarBRL(valorNum);
  }, [negocio, valorNum]);

  const permuta = aceitaPermutaBool(negocio);
  const visitasCount = Number(negocio?.visitasRealizadasCount || 0);
  const flex = useMemo(() => calcFlexibilidade(negocio), [negocio]);
  const bairrosInfo = useMemo(() => bairrosResumo(bairros), [bairros]);

  const localLinha1 = regiao ? `Região: ${regiao}` : cidade;
  const localLinha2 = bairrosInfo.long || bairrosInfo.short;

  const corretorNome =
    negocio?.nomeCorretor ||
    negocio?.corretorNome ||
    negocio?.corretorNomePublico ||
    'Corretor';

  // ✅ Cliente (apenas em Meus Negócios)
  const clienteNomeRaw =
    negocio?.nomeCliente ||
    negocio?.clientePrimeiroNome ||
    negocio?.clienteNome ||
    '';
  const clienteNome = String(clienteNomeRaw || '').trim();

  // Mostra cliente só no padrão "meu"
  const showClienteNoCard = modo === 'meu' && !!clienteNome;

  const whatsappCorretorFinalLink = useMemo(() => {
    if (whatsappCorretorLink) return whatsappCorretorLink;
    const possible =
      negocio?.whatsappCorretor ||
      negocio?.corretorWhatsapp ||
      negocio?.whatsapp ||
      null;
    const msg = `Olá ${corretorNome}! Vi seu negócio na Central de Negócios da Elion.`;
    return buildWhatsAppLink(possible, msg);
  }, [whatsappCorretorLink, negocio, corretorNome]);

  const openDetails = useCallback(() => {
    if (onOpenDetails) return onOpenDetails(negocio);

    const id = negocio?.id || negocio?.negocioId;
    if (id) return navigate(`/negocios/${id}`);
  }, [onOpenDetails, negocio, navigate]);

  const onCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDetails();
    }
  };

  async function handleRegistrarVisita(e) {
    e.stopPropagation();
    if (!usuario?.uid) return toast.error('Faça login para registrar visita.');
    try {
      setLoadingVisita(true);
      await registrarVisitaRealizadaNoNegocio(negocio.id, usuario.uid);
      toast.success('Visita registrada! (+12 pts)');
      onVisitaRegistrada?.(negocio.id);
    } catch (err) {
      toast.error(err?.message || 'Erro ao registrar visita.');
    } finally {
      setLoadingVisita(false);
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openDetails}
      onKeyDown={onCardKeyDown}
      className={[
        'w-full rounded-2xl border bg-white transition-all duration-200',
        'cursor-pointer select-none',
        'hover:-translate-y-[1px] hover:bg-blue-50/40 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-500/10',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30 focus-visible:border-blue-200',
        matchAberto
          ? 'border-blue-200 ring-2 ring-blue-500/10'
          : 'border-zinc-200',
      ].join(' ')}
    >
      <div className={isCompact ? 'px-5 py-4' : 'px-6 py-5'}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {permuta && (
                <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700">
                  <Shuffle className="h-3 w-3" />
                  Permuta
                </span>
              )}

              {modo === 'meu' && visitasCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" />
                  {visitasCount} visita(s)
                </span>
              )}
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-2">
            {modo !== 'meu' &&
              showWhatsAppCorretor &&
              whatsappCorretorFinalLink && (
                <a
                  href={whatsappCorretorFinalLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center justify-center rounded-full h-9 w-9 border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                  title={`Chamar ${corretorNome} no WhatsApp`}
                >
                  <WhatsAppIcon className="h-4 w-4" />
                </a>
              )}
          </div>
        </div>

        <div className="mt-2 grid gap-4 md:grid-cols-12 md:items-center">
          <div className="md:col-span-4 min-w-0">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-extrabold text-zinc-900 text-base">
                {tipoImovel}
              </span>
              <span className="text-zinc-300">•</span>
              <span className="text-zinc-500">{objetivo}</span>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-sm font-extrabold text-zinc-900">{ticket}</div>
            {!isCompact && (
              <div className="mt-0.5 text-xs text-zinc-500">
                {formatFaixaValor(negocio)}
              </div>
            )}
          </div>

          <div className="md:col-span-3 min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
              <span
                className={[
                  'h-2 w-2 rounded-full',
                  flexDotClass(flex.level),
                ].join(' ')}
              />
              <span className="truncate">{flex.label}</span>
            </div>
            <div
              className="mt-0.5 text-xs text-zinc-500 truncate"
              title={localLinha1}
            >
              {localLinha1}
            </div>
            <div className="text-[10px] text-zinc-400 truncate">
              {localLinha2}
            </div>
          </div>

          {/* ✅ AUTOR: no meu mostra CLIENTE, no restante mantém padrão */}
          <div className="md:col-span-2 flex items-center justify-end gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-full border bg-zinc-50 flex items-center justify-center text-[10px] font-bold text-zinc-600">
                {modo !== 'meu' ? (
                  initials(corretorNome)
                ) : (
                  <User2 className="h-4 w-4 text-zinc-400" />
                )}
              </div>

              <div className="hidden lg:block min-w-0">
                {/* Linha principal */}
                <div className="text-xs font-semibold text-zinc-900 truncate">
                  {modo !== 'meu'
                    ? corretorNome
                    : showClienteNoCard
                      ? clienteNome
                      : 'Você'}
                </div>

                {/* Subtexto */}
                <div className="text-[10px] text-zinc-400 truncate">
                  {modo === 'meu'
                    ? showClienteNoCard
                      ? 'Cliente'
                      : 'Clique para ver detalhes'
                    : 'Clique para ver detalhes'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {modo === 'meu' && (
          <div className="mt-4 border-t pt-3 flex items-center justify-between border-zinc-100">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMatchClick?.();
              }}
              className="text-xs font-bold text-zinc-500 flex items-center gap-1 hover:text-blue-600"
            >
              {matchAberto ? 'Ocultar Oportunidades' : 'Ver Oportunidades'}
              <ChevronDown
                className={`h-3 w-3 transition-transform ${matchAberto ? 'rotate-180' : ''}`}
              />
            </button>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditar?.();
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
              >
                <Edit className="h-3.5 w-3.5" /> Editar
              </button>

              <button
                type="button"
                disabled={loadingVisita}
                onClick={handleRegistrarVisita}
                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Visita
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onVerContatos?.();
                }}
                className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
              >
                <MessageCircle className="h-3.5 w-3.5" /> Contatos
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
